import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus, Search, Trash2, Ban, IndianRupee, AlertTriangle, ReceiptText, Percent,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { inr, fmtDate, today } from "@/lib/format";
import { useCanEdit, useHasModule } from "@/context/AuthContext";
import { CardList, DataCard, TableWrap } from "@/components/DataCards";
import Pagination, { usePagedList } from "@/components/Pagination";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const TABS = [["bills", "Bills"], ["ageing", "What I owe"], ["register", "Purchase register"], ["itc", "Input tax credit"]];

const STATUS_TONE = {
  UNPAID: "bg-rose-50 text-rose-700",
  PARTIALLY_PAID: "bg-amber-50 text-amber-700",
  PAID: "bg-emerald-50 text-emerald-700",
  OVERDUE: "bg-rose-100 text-rose-800",
  CANCELLED: "bg-slate-100 text-slate-500",
};

const Tile = ({ icon: Icon, label, value, sub, testid }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid={testid}>
    <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-slate-500">
      <Icon size={13} className="text-blue-500" /> {label}
    </div>
    <div className="mt-2 font-heading text-2xl font-bold tabular-nums">{value}</div>
    {sub && <div className="mt-0.5 text-[11px] text-slate-500">{sub}</div>}
  </div>
);

const blankLine = () => ({
  item_id: null, item_name: "", hsn_sac_code: "", quantity: 1, rate: 0,
  discount_type: "PERCENT", discount_value: 0, tax_rate_percent: 18,
  unit_symbol: "NOS", uqc_code: "NOS-NUMBERS", tax_treatment: "TAXABLE",
});

export default function Purchases() {
  const navigate = useNavigate();
  const canEdit = useCanEdit();
  const hasInventory = useHasModule("inventory");
  const [tab, setTab] = useState("bills");

  const [bills, setBills] = useState([]);
  const [summary, setSummary] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("ALL");

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const [report, setReport] = useState(null);

  const [range, setRange] = useState({ from_date: "", to_date: "" });
  const query = useMemo(() => ({
    q: q || undefined,
    status: status === "ALL" ? undefined : status,
    from_date: range.from_date || undefined,
    to_date: range.to_date || undefined,
  }), [q, status, range]);

  const { rows: pagedBills, total, page, setPage, size, setSize, loading, reload } =
    usePagedList("/purchases/bills", query);

  useEffect(() => { setBills(pagedBills); }, [pagedBills]);

  const load = useCallback(() => {
    reload();
    api.get("/purchases/summary").then(({ data }) => setSummary(data));
  }, [reload]);

  useEffect(() => {
    api.get("/purchases/summary").then(({ data }) => setSummary(data));
  }, []);
  useEffect(() => {
    api.get("/parties", { params: { kind: "SUPPLIER" } }).then(({ data }) => setSuppliers(data));
    api.get("/items").then(({ data }) => setItems(data));
  }, []);

  useEffect(() => {
    if (tab === "bills") return;
    const url = { ageing: "/purchases/reports/payables-ageing",
                  register: "/purchases/reports/purchase-register",
                  itc: "/purchases/reports/itc-summary" }[tab];
    api.get(url).then(({ data }) => setReport(data));
  }, [tab]);

  // Live totals while the bill is being typed, computed by the same engine
  // that will save it — so what you see is what gets recorded.
  useEffect(() => {
    if (!open || !form?.supplier_id || !form.lines.some((l) => Number(l.quantity) > 0)) {
      setPreview(null);
      return;
    }
    const t = setTimeout(() => {
      api.post("/purchases/bills/preview", { ...form, supplier_bill_number: form.supplier_bill_number || "X" })
        .then(({ data }) => setPreview(data))
        .catch(() => setPreview(null));
    }, 350);
    return () => clearTimeout(t);
  }, [open, form]);

  const openNew = () => {
    setForm({
      supplier_id: "", supplier_bill_number: "", bill_date: today(), due_date: "",
      itc_eligible: true, itc_reason: "", is_reverse_charge: false,
      lines: [blankLine()], document_discount_type: "AMOUNT", document_discount_value: 0,
      notes: "", receive_stock: false, warehouse_id: null,
    });
    setPreview(null);
    setOpen(true);
  };

  const setLine = (i, patch) =>
    setForm((f) => ({ ...f, lines: f.lines.map((l, x) => (x === i ? { ...l, ...patch } : l)) }));

  const pickItem = (i, itemId) => {
    const it = items.find((x) => x.id === itemId);
    if (!it) return;
    setLine(i, {
      item_id: it.id, item_name: it.name, hsn_sac_code: it.hsn_sac_code,
      rate: it.purchase_price || it.sale_price || 0, tax_rate_percent: it.tax_rate_percent,
      unit_symbol: it.unit_symbol, uqc_code: it.uqc_code,
    });
  };

  const save = async () => {
    if (!form.supplier_id) return toast.error("Pick a supplier");
    if (!form.supplier_bill_number.trim()) return toast.error("Enter the supplier's bill number");
    setBusy(true);
    try {
      const payload = {
        ...form,
        due_date: form.due_date || null,
        lines: form.lines.filter((l) => l.item_name && Number(l.quantity) > 0).map((l) => ({
          ...l, quantity: Number(l.quantity) || 0, rate: Number(l.rate) || 0,
          discount_value: Number(l.discount_value) || 0,
          tax_rate_percent: Number(l.tax_rate_percent) || 0,
        })),
      };
      if (!payload.lines.length) { setBusy(false); return toast.error("Add at least one line"); }
      const { data } = await api.post("/purchases/bills", payload);
      toast.success(`Bill ${data.supplier_bill_number} recorded`
        + (data.stock_receipt_id ? " and stock received" : ""));
      setOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
    setBusy(false);
  };

  const cancel = async (b) => {
    const reason = window.prompt(`Cancel bill ${b.supplier_bill_number}?`, "Entered in error");
    if (!reason) return;
    try {
      await api.post(`/purchases/bills/${b.id}/cancel`, { reason });
      toast.success("Bill cancelled");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const t = preview?.totals;

  return (
    <div className="space-y-6" data-testid="purchases-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Purchases</h1>
          <p className="mt-1 text-sm text-slate-500">
            Supplier bills, what you owe, and the input tax credit behind it
          </p>
        </div>
        {canEdit && (
          <Button onClick={openNew} data-testid="new-bill-btn"
            className="gap-1.5 rounded-full bg-blue-600 hover:bg-blue-700">
            <Plus size={16} /> Record bill
          </Button>
        )}
      </div>

      {summary && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Tile icon={IndianRupee} label="To pay" value={inr(summary.to_pay)}
            sub={`${summary.open_bills} open bill${summary.open_bills === 1 ? "" : "s"}`} testid="tile-to-pay" />
          <Tile icon={AlertTriangle} label="Overdue" value={inr(summary.overdue_amount)}
            sub={`${summary.overdue_bills} past due date`} testid="tile-overdue" />
          <Tile icon={ReceiptText} label="This month" value={inr(summary.month_purchases)} testid="tile-month" />
          <Tile icon={Percent} label="ITC this month" value={inr(summary.month_itc)} testid="tile-itc" />
        </div>
      )}

      {!suppliers.length && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800"
          data-testid="no-suppliers-warning">
          You have no suppliers yet. Add one under <strong>Suppliers</strong>, or tick the
          Supplier role on an existing party, before recording a bill.
        </div>
      )}

      <div className="flex flex-wrap gap-1 border-b border-slate-200">
        {TABS.map(([k, label]) => (
          <button key={k} data-testid={`purch-tab-${k}`} onClick={() => setTab(k)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors duration-150 ${
              tab === k ? "border-blue-600 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-800"
            }`}>{label}</button>
        ))}
      </div>

      {tab === "bills" && (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-3">
            <div className="relative w-full sm:w-72">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input data-testid="bills-search" className="h-9 pl-8" value={q}
                onChange={(e) => setQ(e.target.value)} placeholder="Bill number or supplier" />
            </div>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="h-9 w-full sm:w-44" data-testid="bills-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All bills</SelectItem>
                <SelectItem value="OPEN">Unpaid only</SelectItem>
                <SelectItem value="OVERDUE">Overdue only</SelectItem>
                <SelectItem value="CANCELLED">Cancelled</SelectItem>
              </SelectContent>
            </Select>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase text-slate-500">From</Label>
              <Input type="date" className="h-9 w-36" data-testid="bills-from" value={range.from_date}
                onChange={(e) => setRange((r) => ({ ...r, from_date: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase text-slate-500">To</Label>
              <Input type="date" className="h-9 w-36" data-testid="bills-to" value={range.to_date}
                onChange={(e) => setRange((r) => ({ ...r, to_date: e.target.value }))} />
            </div>
          </div>

          <TableWrap>
            <table className="w-full text-sm" data-testid="bills-table">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-2.5 text-left font-semibold">Bill no.</th>
                  <th className="px-3 py-2.5 text-left font-semibold">Supplier</th>
                  <th className="px-3 py-2.5 text-left font-semibold">Date</th>
                  <th className="px-3 py-2.5 text-left font-semibold">Due</th>
                  <th className="px-3 py-2.5 text-right font-semibold">Taxable</th>
                  <th className="px-3 py-2.5 text-right font-semibold">Tax</th>
                  <th className="px-3 py-2.5 text-right font-semibold">Total</th>
                  <th className="px-3 py-2.5 text-right font-semibold">Balance</th>
                  <th className="px-4 py-2.5 text-right font-semibold">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {bills.map((b) => (
                  <tr key={b.id} onClick={() => navigate(`/purchases/bills/${b.id}`)}
                    className="cursor-pointer hover:bg-blue-50/40 transition-colors duration-150"
                    data-testid={`bill-row-${b.id}`}>
                    <td className="px-4 py-2.5 font-mono text-xs font-semibold">
                      {b.supplier_bill_number}
                      {!b.itc_eligible && (
                        <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] uppercase text-slate-600">
                          No ITC
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2.5">{b.supplier_name}</td>
                    <td className="px-3 py-2.5 whitespace-nowrap text-xs text-slate-600">{fmtDate(b.bill_date)}</td>
                    <td className="px-3 py-2.5 whitespace-nowrap text-xs text-slate-600">{fmtDate(b.due_date)}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-xs">{inr(b.totals?.taxable_value)}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-xs">{inr(b.totals?.total_tax)}</td>
                    <td className="px-3 py-2.5 text-right font-mono font-semibold">{inr(b.totals?.grand_total)}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-xs text-rose-700">
                      {b.balance_due > 0.01 ? inr(b.balance_due) : "—"}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <span className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${STATUS_TONE[b.payment_status]}`}>
                        {b.payment_status?.replace("_", " ")}
                      </span>
                      {canEdit && b.status === "RECORDED" && b.amount_paid < 0.01 && (
                        <button
                          onClick={(e) => { e.stopPropagation(); cancel(b); }}
                          data-testid={`bill-cancel-${b.id}`}
                          className="ml-2 text-slate-400 hover:text-rose-600" title="Cancel bill">
                          <Ban size={13} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {!bills.length && (
                  <tr><td colSpan={9} className="px-4 py-14 text-center text-sm text-slate-500">
                    No bills recorded yet
                  </td></tr>
                )}
              </tbody>
            </table>
          </TableWrap>

          <CardList empty="No bills recorded yet" testid="bills-cards">
            {bills.map((b) => (
              <DataCard key={b.id} testid={`bill-card-${b.id}`}
                onClick={() => navigate(`/purchases/bills/${b.id}`)}
                title={b.supplier_bill_number} subtitle={b.supplier_name}
                right={
                  <>
                    <div className="font-mono text-sm font-bold tabular-nums">{inr(b.totals?.grand_total)}</div>
                    <span className={`mt-1 inline-block rounded px-1.5 py-0.5 text-[10px] font-medium ${STATUS_TONE[b.payment_status]}`}>
                      {b.payment_status?.replace("_", " ")}
                    </span>
                  </>
                }
                rows={[
                  ["Date", fmtDate(b.bill_date)],
                  ["Due", fmtDate(b.due_date)],
                  b.balance_due > 0.01 ? ["Balance", <span className="text-rose-700">{inr(b.balance_due)}</span>] : null,
                  ["ITC", b.itc_eligible ? inr(b.input_tax_credit) : "Not claimable"],
                ]}
              />
            ))}
          </CardList>

          <Pagination page={page} setPage={setPage} size={size} setSize={setSize}
            total={total} loading={loading} testid="bills-pagination" />
        </div>
      )}

      {tab === "ageing" && report && (
        <div className="overflow-x-auto rounded-md border border-slate-200 bg-white">
          <table className="w-full text-sm" data-testid="payables-ageing-table">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2.5 text-left font-semibold">Supplier</th>
                {report.buckets?.map((b) => (
                  <th key={b} className="px-3 py-2.5 text-right font-semibold">{b}</th>
                ))}
                <th className="px-4 py-2.5 text-right font-semibold">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {report.rows?.map((r) => (
                <tr key={r.supplier_id}>
                  <td className="px-4 py-2.5">{r.supplier_name}</td>
                  {report.buckets.map((b) => (
                    <td key={b} className="px-3 py-2.5 text-right font-mono text-xs">
                      {r[b] ? inr(r[b]) : "—"}
                    </td>
                  ))}
                  <td className="px-4 py-2.5 text-right font-mono font-semibold">{inr(r.total)}</td>
                </tr>
              ))}
              {!report.rows?.length && (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-sm text-slate-500">
                  You owe nothing right now
                </td></tr>
              )}
            </tbody>
            {!!report.rows?.length && (
              <tfoot>
                <tr className="bg-slate-900 text-white">
                  <td className="px-4 py-2.5 text-xs font-semibold uppercase tracking-widest">Total owed</td>
                  {report.buckets.map((b) => (
                    <td key={b} className="px-3 py-2.5 text-right font-mono text-xs">{inr(report.totals[b])}</td>
                  ))}
                  <td className="px-4 py-2.5 text-right font-mono font-bold">{inr(report.grand_total)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}

      {tab === "register" && report && (
        <div className="overflow-x-auto rounded-md border border-slate-200 bg-white">
          <div className="border-b border-slate-100 px-4 py-2.5 text-xs text-slate-500">
            {fmtDate(report.from_date)} — {fmtDate(report.to_date)}
          </div>
          <table className="w-full text-sm" data-testid="purchase-register-table">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2.5 text-left font-semibold">Date</th>
                <th className="px-3 py-2.5 text-left font-semibold">Bill</th>
                <th className="px-3 py-2.5 text-left font-semibold">Supplier</th>
                <th className="px-3 py-2.5 text-left font-semibold">GSTIN</th>
                <th className="px-3 py-2.5 text-right font-semibold">Taxable</th>
                <th className="px-3 py-2.5 text-right font-semibold">CGST</th>
                <th className="px-3 py-2.5 text-right font-semibold">SGST</th>
                <th className="px-3 py-2.5 text-right font-semibold">IGST</th>
                <th className="px-4 py-2.5 text-right font-semibold">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {report.rows?.map((r) => (
                <tr key={r.id}>
                  <td className="px-4 py-2.5 whitespace-nowrap text-xs">{fmtDate(r.bill_date)}</td>
                  <td className="px-3 py-2.5 font-mono text-xs">{r.supplier_bill_number}</td>
                  <td className="px-3 py-2.5">{r.supplier_name}</td>
                  <td className="px-3 py-2.5 font-mono text-[11px] text-slate-500">{r.supplier_gstin || "—"}</td>
                  <td className="px-3 py-2.5 text-right font-mono text-xs">{inr(r.taxable_value)}</td>
                  <td className="px-3 py-2.5 text-right font-mono text-xs">{inr(r.cgst)}</td>
                  <td className="px-3 py-2.5 text-right font-mono text-xs">{inr(r.sgst)}</td>
                  <td className="px-3 py-2.5 text-right font-mono text-xs">{inr(r.igst)}</td>
                  <td className="px-4 py-2.5 text-right font-mono font-semibold">{inr(r.total)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-slate-900 text-white">
                <td colSpan={4} className="px-4 py-2.5 text-xs font-semibold uppercase tracking-widest">Total</td>
                <td className="px-3 py-2.5 text-right font-mono text-xs">{inr(report.totals?.taxable_value)}</td>
                <td className="px-3 py-2.5 text-right font-mono text-xs">{inr(report.totals?.cgst)}</td>
                <td className="px-3 py-2.5 text-right font-mono text-xs">{inr(report.totals?.sgst)}</td>
                <td className="px-3 py-2.5 text-right font-mono text-xs">{inr(report.totals?.igst)}</td>
                <td className="px-4 py-2.5 text-right font-mono font-bold">{inr(report.totals?.total)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {tab === "itc" && report && (
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="rounded-lg border border-slate-200 bg-white p-5" data-testid="itc-claimable">
            <div className="text-[11px] uppercase tracking-wide text-slate-500">Claimable this period</div>
            <div className="mt-1 font-heading text-3xl font-extrabold tabular-nums text-emerald-700">
              {inr(report.claimable)}
            </div>
            <div className="mt-3 space-y-1 border-t border-slate-100 pt-3 text-xs text-slate-600">
              <div className="flex justify-between"><span>CGST</span><span className="font-mono">{inr(report.by_head?.cgst)}</span></div>
              <div className="flex justify-between"><span>SGST</span><span className="font-mono">{inr(report.by_head?.sgst)}</span></div>
              <div className="flex justify-between"><span>IGST</span><span className="font-mono">{inr(report.by_head?.igst)}</span></div>
              <div className="flex justify-between border-t border-slate-100 pt-1 text-slate-500">
                <span>From</span><span>{report.eligible_bills} bill(s)</span>
              </div>
            </div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-5" data-testid="itc-blocked">
            <div className="text-[11px] uppercase tracking-wide text-slate-500">Blocked / not claimed</div>
            <div className="mt-1 font-heading text-3xl font-extrabold tabular-nums text-slate-400">
              {inr(report.blocked)}
            </div>
            <p className="mt-3 border-t border-slate-100 pt-3 text-xs text-slate-500">
              Tax on {report.blocked_bills} bill(s) marked not eligible for credit. It is a cost to
              the business, not a credit against output tax.
            </p>
          </div>
        </div>
      )}

      {/* ---------------- record bill ---------------- */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto" data-testid="bill-dialog">
          <DialogHeader>
            <DialogTitle>Record a supplier bill</DialogTitle>
            <DialogDescription>
              This is the supplier&rsquo;s own bill number, not one of yours — it never touches your
              invoice series.
            </DialogDescription>
          </DialogHeader>

          {form && (
            <div className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <div className="space-y-1.5 lg:col-span-2">
                  <Label className="text-xs">Supplier</Label>
                  <Select value={form.supplier_id}
                    onValueChange={(v) => setForm((f) => ({ ...f, supplier_id: v }))}>
                    <SelectTrigger data-testid="bill-supplier"><SelectValue placeholder="Pick a supplier" /></SelectTrigger>
                    <SelectContent>
                      {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.display_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Their bill number</Label>
                  <Input data-testid="bill-number" className="font-mono" value={form.supplier_bill_number}
                    onChange={(e) => setForm((f) => ({ ...f, supplier_bill_number: e.target.value }))} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Bill date</Label>
                  <Input type="date" data-testid="bill-date" value={form.bill_date}
                    onChange={(e) => setForm((f) => ({ ...f, bill_date: e.target.value }))} />
                </div>
              </div>

              <div className="overflow-x-auto rounded-md border border-slate-200">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-2 py-2 text-left font-semibold w-[30%]">Item</th>
                      <th className="px-2 py-2 text-left font-semibold">HSN</th>
                      <th className="px-2 py-2 text-right font-semibold">Qty</th>
                      <th className="px-2 py-2 text-right font-semibold">Rate</th>
                      <th className="px-2 py-2 text-right font-semibold">Disc %</th>
                      <th className="px-2 py-2 text-right font-semibold">GST %</th>
                      <th className="px-2 py-2 text-right font-semibold">Taxable</th>
                      <th className="px-2 py-2" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {form.lines.map((l, i) => (
                      <tr key={i} className="align-top" data-testid={`bill-line-${i}`}>
                        <td className="space-y-1 px-2 py-2">
                          <Select value={l.item_id || ""} onValueChange={(v) => pickItem(i, v)}>
                            <SelectTrigger className="h-8 text-xs" data-testid={`bill-item-${i}`}>
                              <SelectValue placeholder="Pick item" />
                            </SelectTrigger>
                            <SelectContent>
                              {items.map((it) => <SelectItem key={it.id} value={it.id}>{it.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          <Input className="h-7 text-xs" placeholder="Description" value={l.item_name}
                            data-testid={`bill-name-${i}`}
                            onChange={(e) => setLine(i, { item_name: e.target.value })} />
                        </td>
                        <td className="px-2 py-2">
                          <Input className="h-8 w-20 font-mono text-xs" value={l.hsn_sac_code}
                            data-testid={`bill-hsn-${i}`}
                            onChange={(e) => setLine(i, { hsn_sac_code: e.target.value })} />
                        </td>
                        <td className="px-2 py-2">
                          <Input className="h-8 w-16 text-right font-mono text-xs" inputMode="decimal"
                            value={l.quantity} data-testid={`bill-qty-${i}`}
                            onChange={(e) => setLine(i, { quantity: e.target.value })} />
                        </td>
                        <td className="px-2 py-2">
                          <Input className="h-8 w-24 text-right font-mono text-xs" inputMode="decimal"
                            value={l.rate} data-testid={`bill-rate-${i}`}
                            onChange={(e) => setLine(i, { rate: e.target.value })} />
                        </td>
                        <td className="px-2 py-2">
                          <Input className="h-8 w-16 text-right font-mono text-xs" inputMode="decimal"
                            value={l.discount_value}
                            onChange={(e) => setLine(i, { discount_value: e.target.value, discount_type: "PERCENT" })} />
                        </td>
                        <td className="px-2 py-2">
                          <Select value={String(l.tax_rate_percent)}
                            onValueChange={(v) => setLine(i, { tax_rate_percent: Number(v) })}>
                            <SelectTrigger className="h-8 w-20 text-xs" data-testid={`bill-tax-${i}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {[0, 5, 12, 18, 28].map((r) => <SelectItem key={r} value={String(r)}>{r}%</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-2 py-3 text-right font-mono tabular-nums">
                          {inr(preview?.lines?.[i]?.taxable_value || 0)}
                        </td>
                        <td className="px-2 py-3 text-right">
                          {form.lines.length > 1 && (
                            <button onClick={() => setForm((f) => ({ ...f, lines: f.lines.filter((_, x) => x !== i) }))}
                              data-testid={`bill-remove-${i}`} className="text-slate-400 hover:text-rose-600">
                              <Trash2 size={14} />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex flex-wrap items-start justify-between gap-4">
                <Button size="sm" variant="outline" className="h-8 gap-1 text-xs" data-testid="bill-add-line"
                  onClick={() => setForm((f) => ({ ...f, lines: [...f.lines, blankLine()] }))}>
                  <Plus size={13} /> Add line
                </Button>
                {t && (
                  <div className="w-full max-w-xs space-y-1 text-sm sm:w-auto" data-testid="bill-totals">
                    <div className="flex justify-between"><span className="text-slate-600">Taxable</span>
                      <span className="font-mono">{inr(t.taxable_value)}</span></div>
                    {t.cgst_amount > 0 && (
                      <>
                        <div className="flex justify-between"><span className="text-slate-600">CGST</span>
                          <span className="font-mono">{inr(t.cgst_amount)}</span></div>
                        <div className="flex justify-between"><span className="text-slate-600">SGST</span>
                          <span className="font-mono">{inr(t.sgst_amount)}</span></div>
                      </>
                    )}
                    {t.igst_amount > 0 && (
                      <div className="flex justify-between"><span className="text-slate-600">IGST</span>
                        <span className="font-mono">{inr(t.igst_amount)}</span></div>
                    )}
                    <div className="flex justify-between border-t border-slate-200 pt-1 font-semibold">
                      <span>Total</span><span className="font-mono">{inr(t.grand_total)}</span>
                    </div>
                  </div>
                )}
              </div>

              <div className="grid gap-3 rounded-md border border-slate-200 p-3 sm:grid-cols-2">
                <label className="flex items-start justify-between gap-3 text-sm">
                  <span>
                    <span className="font-medium">Input tax credit claimable</span>
                    <span className="block text-[11px] text-slate-500">
                      Turn off for blocked purchases under s.17(5) — the tax becomes a cost instead.
                    </span>
                  </span>
                  <Switch data-testid="bill-itc" checked={form.itc_eligible}
                    onCheckedChange={(v) => setForm((f) => ({ ...f, itc_eligible: v }))} />
                </label>
                {hasInventory && (
                  <label className="flex items-start justify-between gap-3 text-sm">
                    <span>
                      <span className="font-medium">Receive into stock</span>
                      <span className="block text-[11px] text-slate-500">
                        Raises stock for tracked items at this bill&rsquo;s unit costs.
                      </span>
                    </span>
                    <Switch data-testid="bill-receive-stock" checked={form.receive_stock}
                      onCheckedChange={(v) => setForm((f) => ({ ...f, receive_stock: v }))} />
                  </label>
                )}
                {!form.itc_eligible && (
                  <div className="space-y-1.5 sm:col-span-2">
                    <Label className="text-xs">Why is credit not claimable?</Label>
                    <Input data-testid="bill-itc-reason" value={form.itc_reason}
                      onChange={(e) => setForm((f) => ({ ...f, itc_reason: e.target.value }))}
                      placeholder="e.g. Motor vehicle, s.17(5)(a)" />
                  </div>
                )}
                <div className="space-y-1.5 sm:col-span-2">
                  <Label className="text-xs">Notes</Label>
                  <Textarea rows={2} data-testid="bill-notes" value={form.notes}
                    onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={busy} data-testid="bill-save"
              className="bg-blue-600 hover:bg-blue-700">Record bill</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
